import { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useLightboxBehavior } from "@/hooks/useLightboxBehavior";
import { toast } from "sonner";
import { PHONE_HREF, PHONE_DISPLAY, MESSENGER_URL, SMS_DRAFTS } from "@/lib/contact";
import { useSmsHref } from "@/components/site/ContactButtons";
import { Phone, MessageCircle } from "lucide-react";
import { track } from "@/lib/track";

const inputCls =
  "w-full px-4 py-3 bg-white border border-[#D5CEBD] text-sm text-[#2C2A28] placeholder:text-[#6F6A60] focus:outline-none focus:border-[#7A6547]";
const labelCls = "block text-[10px] tracking-[0.24em] uppercase text-[#5C5955] mb-1.5";

export const ReserveModal = ({ open, onClose, piece, mode = "availability", smsBody }) => {
  const [form, setForm] = useState({ name: "", phone: "", note: "" });
  const textHref = useSmsHref(smsBody || (mode === "maintenance" ? SMS_DRAFTS.service : SMS_DRAFTS.general + piece));
  useLightboxBehavior(open, { onClose });
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const PREFIX = { reserve: "RESERVE", maintenance: "MAINTENANCE REQUEST", majestic: "MAJESTIC PLAN" };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reserve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          piece: `${PREFIX[mode] || "INQUIRY"}: ${piece}`,
          note: form.note,
        }),
      });
      if (!res.ok) throw new Error("failed");
      track(
        mode === "reserve" ? "reserve_submit" : mode === "maintenance" ? "maintenance_request" : mode === "majestic" ? "majestic_request" : "inquiry_submit",
        piece
      );
      toast.success("Request sent! Chris will call or text you shortly.");
      onClose();
    } catch {
      toast.error(`Could not send right now. Please call Chris at ${PHONE_DISPLAY}.`);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1150] flex items-end sm:items-center justify-center bg-[#1F1E1C]/70 backdrop-blur-sm p-0 sm:p-6"
      onClick={onClose}
      data-testid="reserve-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reserve-modal-title"
        className="w-full sm:max-w-md max-h-[92vh] overflow-y-auto bg-[#F9F6F0] p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close dialog"
          data-testid="reserve-modal-close"
          className="absolute top-4 right-4 p-1 text-[#5C5955] hover:text-[#2C2A28]"
        >
          <X size={18} strokeWidth={1.5} />
        </button>
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#7A6547] mb-2">
          {mode === "reserve" ? "Reserve this piece" : mode === "maintenance" ? "Request maintenance" : mode === "majestic" ? "Plan your Majestic" : "Inquire about this piece"}
        </div>
        <h3 id="reserve-modal-title" className="font-serif text-2xl text-[#2C2A28] font-light mb-1">{piece}</h3>
        <div className="grid grid-cols-2 gap-3 mb-2" data-testid="reserve-direct-actions">
          <a
            href={PHONE_HREF}
            onClick={() => track("call_click", `Reserve modal: ${piece}`)}
            data-testid="reserve-call-button"
            aria-label={`Call Chris at ${PHONE_DISPLAY}`}
            className="inline-flex items-center justify-center gap-2 py-3.5 min-h-[44px] bg-[#1F1E1C] text-[#F9F6F0] text-[11px] tracking-[0.2em] uppercase hover:bg-[#7A6547] transition-colors"
          >
            <Phone size={13} strokeWidth={1.5} aria-hidden="true" />
            Call Chris
          </a>
          <a
            href={textHref}
            onClick={() => track("text_click", `Reserve modal: ${piece}`)}
            data-testid="reserve-text-button"
            aria-label={`Text Chris at ${PHONE_DISPLAY}`}
            className="inline-flex items-center justify-center gap-2 py-3.5 min-h-[44px] border border-[#7A6547] text-[#7A6547] text-[11px] tracking-[0.2em] uppercase hover:bg-[#7A6547] hover:text-[#F9F6F0] transition-colors"
          >
            <MessageCircle size={13} strokeWidth={1.5} aria-hidden="true" />
            Text Chris
          </a>
        </div>
        <p className="text-xs text-[#6F6A60] mb-5">
          {PHONE_DISPLAY}
          {mode === "maintenance"
            ? " · Texting opens your messaging app, where you can attach a photo of the fountain."
            : " · Texting opens your messaging app with a short note about this piece filled in."}
        </p>
        <div className="mb-4 flex items-center gap-3 text-[10px] tracking-[0.24em] uppercase text-[#8A867F]">
          <span className="h-px flex-1 bg-[#D5CEBD]" />
          Or leave your number
          <span className="h-px flex-1 bg-[#D5CEBD]" />
        </div>
        <p className="text-[#5C5955] text-sm leading-relaxed mb-5">
          {mode === "maintenance"
            ? "Leave your name and number and Chris will call to set up a visit. Pump swaps, cleaning, winterizing, resealing, and repairs."
            : mode === "majestic"
              ? "Leave your name and number and Chris will call to talk through your Majestic: options, finish, site, and delivery."
              : mode === "reserve"
              ? "Leave your name and number and Chris will call or text to hold it for you and sort out finish, pickup, or delivery."
              : "Leave your name and number and Chris will call or text to confirm it's still available and go over finish, pickup, or delivery."}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label htmlFor="reserve-name" className={labelCls}>Your name</label>
            <input
              id="reserve-name"
              autoFocus
              autoComplete="name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="First and last name"
              data-testid="reserve-name-input"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="reserve-phone" className={labelCls}>Phone number</label>
            <input
              id="reserve-phone"
              autoComplete="tel"
              required
              type="tel"
              minLength={7}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(678) 555-0123"
              data-testid="reserve-phone-input"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="reserve-note" className={labelCls}>Notes (optional)</label>
            <textarea
              id="reserve-note"
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder={mode === "maintenance" ? "What needs attention, and your area" : mode === "majestic" ? "Your town, where it would go, questions" : "Preferred finish, delivery zip, questions"}
              data-testid="reserve-note-input"
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="reserve-submit-button"
            className="w-full py-3.5 bg-[#1F1E1C] text-[#F9F6F0] text-[11px] tracking-[0.22em] uppercase hover:bg-[#7A6547] transition-colors disabled:opacity-60"
          >
            {busy ? "Sending..." : mode === "reserve" ? "Send Reservation Request" : mode === "maintenance" ? "Request Maintenance" : mode === "majestic" ? "Send to Chris" : "Send Inquiry"}
          </button>
        </form>
        <div className="mt-4 flex flex-col sm:flex-row sm:justify-center gap-2 sm:gap-6 text-center text-[#7A6547] text-[11px] tracking-[0.14em] uppercase">
          <a
            href={MESSENGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("messenger_click", `Reserve modal: ${piece}`)}
            className="hover:text-[#5E4D35] whitespace-nowrap"
            data-testid="reserve-messenger-instead"
          >
            Message on Facebook
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
};
